import React, { useRef } from "react";
import { PDFViewer as PaperviewPDF } from "@paperviewpdf/react-viewer";
import httpClient from "../../lib/HttpClient";
import security from "../../services/Security";
import { loadDocumentUrl } from "../../lib/axios-client";
import styles from "./GuestViewer.module.scss";
import {
  PIN_TYPES,
  getPinTools,
  importPinXfdf,
  isPinAnnotation,
  registerPinCommands,
  togglePinTool,
  updatePinToolbar,
} from "../../pdf/pinDrop";

// Guests can only drop comment pins; references are not available to them.
var GUEST_PIN_TYPES = [PIN_TYPES.COMMENT];
var GUEST_PIN_TOOLS = getPinTools(GUEST_PIN_TYPES);

/**
 * Generate a human-readable annotation ID.
 * PaperView PDF does NOT auto-generate IDs — callers must provide one.
 * Format: d{docId}_u{userId}_p{page}_{random}
 *
 * @param {number|string} docId - Document ID
 * @param {number|string} userId - User ID
 * @param {number} pageIndex - Zero-based page index
 * @returns {string} Human-readable annotation ID
 */
function generateAnnotationId(docId, userId, pageIndex) {
  var rand = Math.random().toString(36).substr(2, 5);
  return "d" + docId + "_u" + userId + "_p" + pageIndex + "_" + rand;
}

/**
 * PaperView PDF-based guest viewer for external document review.
 *
 * Features:
 * - Document viewing with zoom, search, pan
 * - Text selection with Comment and Strikeout actions
 * - Pin drop comments (toolbar pin button)
 * - Conversation annotation import (XFDF)
 * - Annotation click opens comment panel in parent
 * - Annotation drag/resize saves to backend (debounced)
 *
 * @param {Object} props
 * @param {string} props.hashUrl - Share link hash for API calls
 * @param {number} props.documentId - Document ID for annotation IDs
 * @param {number} props.guestUserId - Guest user ID for annotation IDs
 * @param {Object} props.approval - Approval object (controls container styling)
 * @param {Array} props.conversations - Conversations with XFDF to import
 * @param {Function} props.setConversationSelected - Callback: set selected annotation ID
 * @param {Function} props.setCurrentXFDFHighlightString - Callback: pass XFDF to parent
 * @param {Function} props.setCurrentPageIndex - Callback: pass page number to parent
 * @param {Function} props.setError - Callback: signal load error
 * @param {Function} props.reloadConversations - Callback: refresh conversations list
 * @param {Object} props.deleteAnnotationRef - Ref that receives deleteAnnotationById function
 */
function PDFTGuestViewer(props) {
  var hashUrl = props.hashUrl;

  // Plugin references — set in handleViewerReady
  var annotationPluginRef = useRef(null);
  var selectionPluginRef = useRef(null);
  var xfdfPluginRef = useRef(null);
  var commandsPluginRef = useRef(null);
  var uiPluginRef = useRef(null);
  var documentManagerPluginRef = useRef(null);

  // Mutable refs for values accessed inside event callbacks.
  // Using refs instead of state because event subscriptions capture
  // closures and would see stale state values otherwise.
  var currentPageIndexRef = useRef(null);
  var currentAnnotationTextRef = useRef("");

  // Debounce timers for annotation update events
  var pendingUpdateTimersRef = useRef({});

  // Ids of pins dropped in this session. Until their comment is saved,
  // moving them must update the XFDF that is waiting to be saved.
  var droppedPinIdsRef = useRef({});

  // Latest conversations, readable from event callbacks (see note above).
  var conversationsRef = useRef(props.conversations);
  conversationsRef.current = props.conversations;

  // Build document URL with auth token
  var documentUrl = loadDocumentUrl() + "/share_links/view/" + hashUrl;

  // =============================================
  // Viewer Initialization
  // =============================================

  /**
   * Called when PaperView PDF viewer is ready.
   * Sets up plugin references, selection commands, event subscriptions,
   * and imports existing conversation annotations.
   *
   * Initialization order matters:
   * 1. Store plugin references
   * 2. Expose deleteAnnotationById to parent
   * 3. Register selection commands (Comment, Strikeout)
   * 4. Configure selection menu
   * 5. Subscribe to selection events
   * 6. Import annotations (delayed for page dimensions)
   * 7. Subscribe to annotation events (after import)
   */
  function handleViewerReady(registry) {
    var annotationProvider = registry.getPlugin("annotation");
    if (annotationProvider) {
      annotationPluginRef.current = annotationProvider.provides();
    }

    var selectionProvider = registry.getPlugin("selection");
    if (selectionProvider) {
      selectionPluginRef.current = selectionProvider.provides();
    }

    var xfdfProvider = registry.getPlugin("xfdf");
    if (xfdfProvider) {
      xfdfPluginRef.current = xfdfProvider.provides();
    }

    var commandsProvider = registry.getPlugin("commands");
    if (commandsProvider) {
      commandsPluginRef.current = commandsProvider.provides();
    }

    var uiProvider = registry.getPlugin("ui");
    if (uiProvider) {
      uiPluginRef.current = uiProvider.provides();
    }

    var documentManagerProvider = registry.getPlugin("document-manager");
    if (documentManagerProvider) {
      documentManagerPluginRef.current = documentManagerProvider.provides();
    }

    // Expose delete function to parent via ref
    if (props.deleteAnnotationRef) {
      props.deleteAnnotationRef.current = deleteAnnotationById;
    }

    registerSelectionCommands();
    updateSelectionMenu();
    subscribeToSelectionEvents();

    // Pin drop toolbar button (comment pin)
    registerPinCommands({
      commandsPlugin: commandsPluginRef.current,
      uiPlugin: uiPluginRef.current,
      pinTypes: GUEST_PIN_TYPES,
      onTogglePinTool: function (pinType) {
        togglePinTool(annotationPluginRef.current, pinType);
      },
    });
    updatePinToolbar({
      uiPlugin: uiPluginRef.current,
      pinTypes: GUEST_PIN_TYPES,
      isEnabled: true,
    });

    // TODO: Replace setTimeout with proper PaperView PDF "pages rendered" event.
    // Without this delay, annotations render in wrong positions because page
    // dimensions aren't ready yet.
    setTimeout(function () {
      loadAnnotations();

      // Subscribe to events after a small delay so imported annotations
      // don't trigger the lifecycle handler.
      setTimeout(function () {
        subscribeToAnnotationEvents();
        subscribeToAnnotationLifecycleEvents();
      }, 200);
    }, 500);
  }

  /**
   * Called when PaperView PDF encounters a load error.
   */
  function handleViewerError() {
    props.setError(true);
  }

  // =============================================
  // XFDF Import
  // =============================================

  /**
   * Imports a single XFDF string into the viewer.
   * importXfdf does NOT return a Promise — do not chain .then().
   *
   * @param {string} xfdfString - XFDF or command-format string
   */
  function importXfdfString(xfdfString) {
    if (!xfdfPluginRef.current || !xfdfString) return;
    try {
      xfdfPluginRef.current.importXfdf(xfdfString);
    } catch (error) {
      // Import failed — viewer still usable
    }
  }

  /**
   * Imports the annotation of a conversation.
   *
   * Pin drops (stamps) cannot go through the xfdf plugin: it does not carry
   * the stamp's image, so the pin would be imported without an appearance
   * and stay invisible. Pins are imported with their image by importPinXfdf;
   * everything else goes through importXfdfString.
   *
   * @param {string} xfdfString - the conversation's xfdf_highlight_string
   * @param {string} annotationId - the conversation's xfdf_highlight_id
   */
  function importConversationXfdf(xfdfString, annotationId) {
    importPinXfdf({
      annotationPlugin: annotationPluginRef.current,
      documentManagerPlugin: documentManagerPluginRef.current,
      xfdfString: xfdfString,
      annotationId: annotationId,
      type: PIN_TYPES.COMMENT,
    })
      .catch(function () {
        return false;
      })
      .then(function (isPinImported) {
        if (!isPinImported) {
          importXfdfString(xfdfString);
        }
      });
  }

  /**
   * Returns true if an annotation with this ID already exists in the viewer.
   * Prevents duplicate imports from bloated legacy XFDF strings that
   * contain all annotations instead of just the current conversation's.
   *
   * @param {string} annotationId - Annotation ID to check
   * @returns {boolean}
   */
  function annotationAlreadyImported(annotationId) {
    if (!annotationPluginRef.current) return false;
    var state = annotationPluginRef.current.getState();
    if (!state || !state.byUid) return false;
    return state.byUid[annotationId] ? true : false;
  }

  /**
   * Imports all conversation annotations from props.conversations.
   * Skips conversations without XFDF data and conversations already
   * imported (deduplication).
   */
  function loadAnnotations() {
    var conversations = props.conversations;
    if (!conversations || !conversations.length) return;

    for (var i = 0; i < conversations.length; i++) {
      var conversation = conversations[i];
      var hasXfdf =
        conversation.xfdf_highlight_id && conversation.xfdf_highlight_string;
      var isNew =
        hasXfdf && !annotationAlreadyImported(conversation.xfdf_highlight_id);

      if (isNew) {
        importConversationXfdf(
          conversation.xfdf_highlight_string,
          conversation.xfdf_highlight_id
        );
      }
    }
  }

  // =============================================
  // Text Selection Commands
  // =============================================

  /**
   * Registers custom commands for the text selection popup.
   * Guest viewer supports Comment and Strikeout actions.
   */
  function registerSelectionCommands() {
    if (!commandsPluginRef.current) return;

    commandsPluginRef.current.registerCommand({
      id: "papercurve:comment",
      label: "Comment",
      icon: "comment",
      action: function () {
        pickComment();
      },
    });

    commandsPluginRef.current.registerCommand({
      id: "papercurve:strikeout",
      label: "Strikeout",
      icon: "strikethrough",
      action: function () {
        pickStrikeout();
      },
    });
  }

  /**
   * Configures the text selection popup with Comment and Strikeout buttons.
   * Uses PaperView PDF's UI plugin schema system.
   */
  function updateSelectionMenu() {
    if (!uiPluginRef.current) return;

    uiPluginRef.current.mergeSchema({
      selectionMenus: {
        selection: {
          id: "selection",
          items: [
            {
              type: "command-button",
              id: "pc-comment",
              commandId: "papercurve:comment",
              variant: "icon",
            },
            {
              type: "command-button",
              id: "pc-strikeout",
              commandId: "papercurve:strikeout",
              variant: "icon",
            },
          ],
        },
      },
    });
  }

  // =============================================
  // Selection Event Tracking
  // =============================================

  /**
   * Subscribes to selection plugin events to track current page index
   * and selected text content. These values are stored in refs so
   * event callbacks always have the latest values.
   */
  function subscribeToSelectionEvents() {
    if (!selectionPluginRef.current) return;

    selectionPluginRef.current.onSelectionChange(function (event) {
      if (event.selection) {
        currentPageIndexRef.current = event.selection.start.page;
      }
    });

    selectionPluginRef.current.onTextRetrieved(function (event) {
      if (event.text) {
        currentAnnotationTextRef.current = event.text.join("");
      }
    });
  }

  // =============================================
  // Annotation Creation from Selection
  // =============================================

  /**
   * Creates an annotation from the current text selection.
   * Used by pickComment and pickStrikeout.
   *
   * @param {string} type - "comment" or "strikeout"
   * @param {string} color - Hex color for the annotation (e.g. "#FFCC99")
   * @param {number} opacity - Opacity value (0-1)
   * @returns {{ annotationId: string, pageIndex: number, xfdf: string } | null}
   *   Returns null if there's no active text selection.
   */
  function createAnnotationFromSelection(type, color, opacity) {
    if (!selectionPluginRef.current || !annotationPluginRef.current)
      return null;

    var selectionState = selectionPluginRef.current.getState();
    if (!selectionState || !selectionState.selection) return null;

    var pageIndex = selectionState.selection.start.page;
    var rects = selectionPluginRef.current.getHighlightRectsForPage(pageIndex);
    if (!rects || rects.length === 0) return null;

    // Compute bounding rect from all selected text segments.
    // Each rect uses PaperView PDF's {origin: {x, y}, size: {width, height}} format.
    var left = rects[0].origin.x;
    var top = rects[0].origin.y;
    var right = rects[0].origin.x + rects[0].size.width;
    var bottom = rects[0].origin.y + rects[0].size.height;

    for (var i = 1; i < rects.length; i++) {
      var r = rects[i];
      if (r.origin.x < left) left = r.origin.x;
      if (r.origin.y < top) top = r.origin.y;
      if (r.origin.x + r.size.width > right) right = r.origin.x + r.size.width;
      if (r.origin.y + r.size.height > bottom)
        bottom = r.origin.y + r.size.height;
    }

    // PdfAnnotationSubtype enum: HIGHLIGHT=9, STRIKEOUT=12
    var annotationType = type === "strikeout" ? 12 : 9;

    // Generate human-readable ID — PaperView PDF does NOT auto-generate IDs
    var annotationId = generateAnnotationId(
      props.documentId,
      props.guestUserId,
      pageIndex
    );

    var annotation = {
      id: annotationId,
      type: annotationType,
      pageIndex: pageIndex,
      rect: {
        origin: { x: left, y: top },
        size: { width: right - left, height: bottom - top },
      },
      color: color || "#FFCC99",
      opacity: opacity != null ? opacity : 0.5,
      segmentRects: rects,
      created: new Date(),
    };

    annotationPluginRef.current.createAnnotation(pageIndex, annotation);

    // Export per-annotation XFDF in command format using a synthetic event
    var xfdfString = "";
    if (xfdfPluginRef.current) {
      try {
        xfdfString = xfdfPluginRef.current.exportAnnotationEvent({
          type: "create",
          annotation: annotation,
          pageIndex: pageIndex,
          documentId: undefined,
          committed: false,
        });
      } catch (e) {
        // XFDF export failed — annotation still created visually
      }
    }

    selectionPluginRef.current.clear();

    return {
      annotationId: annotationId,
      pageIndex: pageIndex,
      xfdf: xfdfString,
    };
  }

  /**
   * Creates a comment highlight annotation from the current text selection.
   * Color: #FFCC99 (orange/peach). Opens the CommentPanel via parent callbacks.
   */
  function pickComment() {
    var result = createAnnotationFromSelection("comment", "#FFCC99", 0.5);
    if (!result) return;

    props.setConversationSelected(null);
    props.setCurrentPageIndex(result.pageIndex + 1);
    props.setConversationSelected(result.annotationId);
    props.setCurrentXFDFHighlightString(result.xfdf);
  }

  /**
   * Creates a strikeout annotation from the current text selection.
   * Saves the conversation to the backend immediately via the guest API.
   */
  function pickStrikeout() {
    var result = createAnnotationFromSelection("strikeout", "#E44234", 1);
    if (!result) return;

    var commentData = {
      hash_url: hashUrl,
      conversation: {
        document_id: props.documentId,
        page_number: result.pageIndex + 1,
        xfdf_highlight_id: result.annotationId,
        xfdf_highlight_string: result.xfdf,
      },
      comment: {
        body: null,
      },
    };

    httpClient.post("/comments/guest.json", commentData).then(function () {
      props.reloadConversations();
    });
  }

  // =============================================
  // Annotation Selection (Click)
  // =============================================

  /**
   * Subscribes to annotation state changes to detect when a user
   * clicks (selects) an annotation. Opens the comment panel for
   * the selected annotation via parent callback.
   */
  function subscribeToAnnotationEvents() {
    if (!annotationPluginRef.current) return;

    var previousSelectedUid = null;

    annotationPluginRef.current.onStateChange(function (stateEvent) {
      var state = stateEvent.state || stateEvent;
      var selectedUid = state.selectedUid;

      if (selectedUid === previousSelectedUid) return;
      previousSelectedUid = selectedUid;

      if (selectedUid) {
        props.setConversationSelected(selectedUid);
      }
    });
  }

  // =============================================
  // Annotation Lifecycle Events (Update)
  // =============================================

  /**
   * Subscribes to annotation lifecycle events (create, update, delete).
   *
   * UPDATE: Debounced save to backend — PaperView PDF fires multiple
   * update events during a single drag operation.
   *
   * CREATE: Skipped for imported annotations. New annotations from
   * text selection are handled by pickComment/pickStrikeout directly.
   * A pin dropped with the pin tool opens the comment panel.
   */
  function subscribeToAnnotationLifecycleEvents() {
    if (!annotationPluginRef.current) return;

    // Snapshot existing annotation IDs (from XFDF import) so we skip them
    var importedAnnotationIds = {};
    var currentState = annotationPluginRef.current.getState();
    if (currentState && currentState.byUid) {
      var uids = Object.keys(currentState.byUid);
      for (var i = 0; i < uids.length; i++) {
        importedAnnotationIds[uids[i]] = true;
      }
    }

    annotationPluginRef.current.onAnnotationEvent(function (event) {
      var annotation = event.annotation;
      if (!annotation) return;

      if (event.type === "update") {
        debouncedAnnotationUpdate(event, annotation);
        return;
      }

      // Skip creates from imported annotations
      if (event.type === "create" && importedAnnotationIds[annotation.id]) {
        return;
      }

      // A pin was dropped with the pin tool. committed=true events are the
      // engine's echo of a create that was already handled.
      var isNewPin =
        event.type === "create" &&
        !event.committed &&
        isPinAnnotation(annotation);
      if (isNewPin) {
        handlePinDropped(event);
      }
    });
  }

  /**
   * Opens the comment panel for a pin that was just dropped. Same parent
   * callbacks as pickComment; the pin already exists, so only its XFDF has
   * to be exported.
   *
   * @param {Object} event - the annotation plugin's create event for the pin
   */
  function handlePinDropped(event) {
    droppedPinIdsRef.current[event.annotation.id] = true;

    var xfdfString = "";
    if (xfdfPluginRef.current) {
      try {
        xfdfString = xfdfPluginRef.current.exportAnnotationEvent(event);
      } catch (e) {
        // XFDF export failed — pin still created visually
      }
    }

    props.setConversationSelected(null);
    props.setCurrentPageIndex(event.pageIndex + 1);
    props.setConversationSelected(event.annotation.id);
    props.setCurrentXFDFHighlightString(xfdfString);
  }

  /**
   * Debounces annotation update events per annotation ID.
   * Only the final position after a drag is exported and saved.
   *
   * @param {Object} event - PaperView PDF annotation event
   * @param {Object} annotation - The annotation being updated
   */
  function debouncedAnnotationUpdate(event, annotation) {
    var annotationId = annotation.id;

    if (pendingUpdateTimersRef.current[annotationId]) {
      clearTimeout(pendingUpdateTimersRef.current[annotationId]);
    }

    pendingUpdateTimersRef.current[annotationId] = setTimeout(function () {
      delete pendingUpdateTimersRef.current[annotationId];

      // A pin whose comment has not been saved yet has nothing to update on
      // the backend: refresh the XFDF waiting to be saved instead, so the
      // pin is saved where the guest left it.
      var isUnsavedPin =
        droppedPinIdsRef.current[annotationId] &&
        !isSavedConversationAnnotation(annotationId);
      if (isUnsavedPin) {
        refreshUnsavedPinXfdf(annotationId, event);
        return;
      }
      delete droppedPinIdsRef.current[annotationId];

      var xfdf = "";
      if (xfdfPluginRef.current) {
        try {
          xfdf = xfdfPluginRef.current.exportAnnotationEvent(event);
        } catch (e) {
          // Export failed
        }
      }
      saveAnnotationUpdate(annotation, xfdf);
    }, 300);
  }

  /**
   * Tells whether an annotation already belongs to a saved conversation.
   *
   * @param {string} annotationId - annotation id (xfdf_highlight_id)
   * @returns {boolean}
   */
  function isSavedConversationAnnotation(annotationId) {
    var conversations = conversationsRef.current;
    if (!conversations || !conversations.length) return false;

    return conversations.some(function (conversation) {
      return conversation.xfdf_highlight_id === annotationId;
    });
  }

  /**
   * Re-exports a dropped pin that is still waiting for its comment to be
   * saved, after the guest moved it, and hands the new XFDF to the parent.
   * Exported from plugin state: the update event still carries the pin's
   * previous position.
   *
   * @param {string} annotationId - id of the pin that was moved
   * @param {Object} event - the annotation plugin's update event
   */
  function refreshUnsavedPinXfdf(annotationId, event) {
    if (!xfdfPluginRef.current || !annotationPluginRef.current) return;

    var state = annotationPluginRef.current.getState();
    var tracked = state && state.byUid && state.byUid[annotationId];
    if (!tracked || !tracked.object) return;

    var xfdfString = "";
    try {
      // Exported as a creation (<add>), the same form it had when dropped.
      xfdfString = xfdfPluginRef.current.exportAnnotationEvent({
        type: "create",
        annotation: tracked.object,
        pageIndex: event.pageIndex,
        documentId: event.documentId,
        committed: false,
      });
    } catch (e) {
      // Export failed — keep the XFDF from when the pin was dropped
    }

    if (xfdfString) {
      props.setCurrentXFDFHighlightString(xfdfString);
    }
  }

  /**
   * Persists an annotation modification (move, resize) to the backend.
   * Uses the guest-specific conversation API endpoint.
   *
   * @param {Object} annotation - The annotation that was modified
   * @param {string} xfdf - The exported XFDF string for this modification
   */
  function saveAnnotationUpdate(annotation, xfdf) {
    var annotationId = annotation.id;

    httpClient
      .put("/conversations/" + annotationId + "/guest.json", {
        hash_url: hashUrl,
        conversation: {
          xfdf_highlight_string: xfdf,
        },
      })
      .catch(function (error) {
        console.error(error);
      });
  }

  // =============================================
  // Annotation Deletion (exposed to parent)
  // =============================================

  /**
   * Deletes an annotation by ID. Called by parent GuestViewer when
   * closing the comment panel with removeHighlight=true (user created
   * a highlight but dismissed the comment panel without saving).
   *
   * Looks up the annotation's pageIndex from the plugin state so
   * the caller doesn't need to provide it.
   *
   * @param {string} annotationId - ID of the annotation to delete
   */
  function deleteAnnotationById(annotationId) {
    if (!annotationPluginRef.current || !annotationId) return;

    var state = annotationPluginRef.current.getState();
    if (!state || !state.byUid) return;

    var tracked = state.byUid[annotationId];
    if (tracked && tracked.object) {
      annotationPluginRef.current.deleteAnnotation(
        tracked.object.pageIndex,
        annotationId
      );
    }
  }

  // =============================================
  // Render
  // =============================================

  var hasApprovalPending =
    Object.keys(props.approval).length > 0 &&
    props.approval.status === "pending";

  var containerClass = hasApprovalPending
    ? styles.containerApprovalsPresent
    : styles.container;

  return (
    <div className={containerClass}>
      <div className={styles.webViewerContainer}>
        <PaperviewPDF
          style={{ width: "100%", height: "100%" }}
          onReady={handleViewerReady}
          onError={handleViewerError}
          config={{
            src: documentUrl,
            wasmUrl: "/pdfium.wasm",
            worker: true,
            theme: {
              preference: "light",
            },
            disabledCategories: [
              "mode-annotate",
              "mode-redact",
              "mode-shapes",
              "redaction",
              "history",
              "document",
              "panel-comment",
            ],
            // Pin drop tool (comment pin)
            annotations: {
              tools: GUEST_PIN_TOOLS,
            },
          }}
        />
      </div>
    </div>
  );
}

export default PDFTGuestViewer;
